import React, { useEffect, useState } from "react";
import {
  useGetAssessmentReports,
  useGetVQAActivity,
  useJoinVqaRequest,
  useGetJoinVQARequestDetails,
  useAcceptVqaRequest,
  useRejectVqaRequest,
  useDocumentAllFiles,
  useDocumentFile,
} from "../../custom-hooks/queries";
import { useNavigate, useParams } from "react-router-dom";

import {
  ArrowDownTrayIcon,
  CalendarDaysIcon,
  CheckBadgeIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  FolderPlusIcon,
  MapPinIcon,
  PaperClipIcon,
} from "@heroicons/react/24/outline";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Icon } from "@iconify/react/dist/iconify.js";
import Skeleton from "react-loading-skeleton";
import { calculateDaysAgo, dateFormat } from "../../utils/helperMethods";
import AdminDetailsLoader from "../../components/Skeletons/adminDetailsLoader";
import Capabilities from "../../components/Capabilities";
import ReportAndActivitySkeletonLoader from "../../components/Skeletons/ReportAndActivitySkeletonLoader";
import StatusProgressBar from "../../components/ProgressBar";
import CustomButton from "../../components/CustomButton";
import Modal from "../../components/Modal";
import NotificationModal from "../../components/NotificationModal";
import StatusForDrawer from "../../components/StatusForDrawer";
import { StatusBadges } from "../../components/StatusBadges";

const schema = yup.object().shape({
  sponsor_contact_name: yup.string(),
  sponsor_contact_email: yup.string().email("Invalid email format"),
  sponsor_contact_phone: yup
    .string()
    .matches(/^[0-9]+$/, "Phone number must contain only numbers")
    .min(10, "Phone number must be at least 10 digits"),
});

export const JoinvqaRequestDetailAssessment = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: assessmentDetails, isLoading } =
    useGetJoinVQARequestDetails(id);

  // Refetch activity when assessment details change
  const { data: activity, refetch: activityRefetch } = useGetVQAActivity(
    assessmentDetails?.vqa_id
  );

  // Modal and comment state
  const [openModal, setOpenModal] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalDescription, setModalDescription] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (assessmentDetails?.vqa_id) {
      activityRefetch();
    }
  }, [assessmentDetails?.vqa_id]);

  // Fetch documents (if available)
  const { data: allDocuments } = useGetAssessmentReports(
    assessmentDetails?.vqa_id
  );
  const {
    mutate,
    data: files,
    isError: isFileError,
    error: errorMessageFile,
  } = useDocumentFile();
  const {
    mutate: allFilesDownload,
    data: allFiles,
    isError: allFilesError,
  } = useDocumentAllFiles();

  const [show, setShow] = useState(false);
  const [message, setMessage] = useState("");

  const handleDownload = (id) => {
    mutate(id, {
      onSuccess: () => {
        setShow(true);
        setMessage("Downloaded Successfully");
      },
      onError: () => {
        setShow(true);
        setMessage("Download Failed");
      },
    });
  };

  const handleDownloadAll = async (ids) => {
    allFilesDownload(ids); // Pass ids directly instead of using docsIds (state update is async)
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const { mutate: acceptVqa } = useAcceptVqaRequest();
  const { mutate: rejectVqa } = useRejectVqaRequest();

  // New function to handle Accept action
  const handleAccept = () => {
    acceptVqa(
      { id },
      {
        onSuccess: () => {
          setModalTitle("Request Accepted!");
          setModalDescription(
            "The join VQA request has been accepted successfully."
          );
          setOpenModal(true);

          setTimeout(() => {
            navigate(
              `/Backend/AssementParticipation/${assessmentDetails?.vqa_id}/List`
            );
          }, 3000);
        },
        onError: (error) => {
          setModalTitle("Error!");
          setModalDescription("Failed to accept the join VQA request.");
          setOpenModal(true);
          setErrorMessage(error);
        },
      }
    );
  };

  // New function to handle Reject action
  const handleReject = () => {
    rejectVqa(
      { id },
      {
        onSuccess: () => {
          setModalTitle("Request Rejected!");
          setModalDescription("The join VQA request has been rejected.");
          setOpenModal(true);
        },
        onError: (error) => {
          // Check if error.response.data exists and extract the message.
          const errorMsg =
            (error?.response &&
              error.response.data &&
              error.response.data.message) ||
            error?.message ||
            "Failed to reject the join VQA request.";
          setErrorMessage(error);
          setModalTitle("Error!");
          setModalDescription(errorMsg);
          setOpenModal(true);
        },
      }
    );
  };
  const additionalInfo = assessmentDetails?.additional_info;

  return (
    <div className="container">
      {/* Header Section */}
      {isLoading ? (
        <Skeleton
          className="w-full h-32 p-7 border-y border-gray-300 bg-slate-50 mb-10"
          enableAnimation={true}
        />
      ) : (
        <div className="p-7 border-y border-gray-300 bg-slate-50 mb-10">
          <div className="flex items-center text-sm">
            <h1
              className="text-gray-500 cursor-pointer font-semibold"
              onClick={() => navigate("/Backend/Assessment/History")}
            >
              Assessments
            </h1>
            <ChevronRightIcon className="w-5 h-5 text-gray-400" />
          </div>

          <div className="mt-5 space-y-2 flex flex-col justify-start items-start md:justify-between md:items-center md:flex-row">
            <div className="flex items-center">
              <span className="bg-white border border-gray-300 rounded-md px-7 py-5 mr-5">
                <CheckBadgeIcon className="h-9 w-9 text-red-500" />
              </span>
              <span>
                <p className="text-sm text-gray-500">
                  {assessmentDetails?.sponsor
                    ? assessmentDetails.provider_name
                    : "-"}
                </p>
                <p className="font-semibold text-md">
                  {assessmentDetails?.sponsor
                    ? assessmentDetails.sponsor.sponsor_name
                    : "-"}
                </p>

                <p className="text-sm text-gray-500">
                  Request Date:{" "}
                  <span className="font-semibold">
                    {assessmentDetails?.join_vqa_request_created_on
                      ? dateFormat(
                          assessmentDetails.join_vqa_request_created_on
                        )
                      : "-"}
                  </span>
                </p>
              </span>
            </div>

            {/* Conditional Rendering of Buttons based on Status */}
            {assessmentDetails?.join_vqa_request_status === "Approved" ||
            assessmentDetails?.join_vqa_request_status === "Rejected" ||
            assessmentDetails?.join_vqa_request_status === "Cancelled" ? (
              <div className="flex gap-4 items-center">
                {assessmentDetails.join_vqa_request_status === "Approved" ? (
                  <CustomButton
                    text="Request Approved"
                    width="100%"
                    height="h-10"
                    paddingRight="10px"
                    weight="font-semibold"
                    size="text-md"
                    color="bg-indigo-600"
                    textColor="text-white"
                    borderRadius="rounded-md"
                    border="none"
                    disabled={true}
                    padding="py-3 px-6"
                  />
                ) : assessmentDetails.join_vqa_request_status ===
                  "Cancelled" ? (
                  <CustomButton
                    text="Cancelled"
                    width="100%"
                    height="h-10"
                    paddingRight="10px"
                    weight="font-semibold"
                    size="text-md"
                    color="bg-red-400"
                    textColor="text-white"
                    borderRadius="rounded-md"
                    border="none"
                    disabled={true}
                    padding="py-3 px-6"
                  />
                ) : (
                  <CustomButton
                    text="Request Rejected"
                    width="100%"
                    height="h-10"
                    paddingRight="10px"
                    weight="font-semibold"
                    size="text-md"
                    color="bg-red-400"
                    textColor="text-white"
                    borderRadius="rounded-md"
                    border="none"
                    disabled={true}
                    padding="py-3 px-6"
                  />
                )}
              </div>
            ) : (
              <div className="flex gap-4">
                <CustomButton
                  text="Accept"
                  width="w-[120px]"
                  height="h-10"
                  weight="font-semibold"
                  size="text-md"
                  color="bg-indigo-600 hover:bg-indigo-700 transition duration-300 ease-in-out"
                  textColor="text-white"
                  borderRadius="rounded-md"
                  border="border border-indigo-700"
                  onClick={handleAccept}
                />
                <CustomButton
                  text="Cancel"
                  width="w-[120px]"
                  height="h-10"
                  weight="font-semibold"
                  size="text-md"
                  color="bg-red-400 hover:bg-red-700 transition duration-300 ease-in-out"
                  textColor="text-white"
                  borderRadius="rounded-md"
                  border="border border-red-700"
                  onClick={handleReject}
                />
              </div>
            )}
          </div>
        </div>
      )}

      <div className="mb-28 px-5 flex justify-center md:justify-between items-stretch flex-col md:flex-row">
        {/* Left Side: Assessment Details */}
        {isLoading ? (
          <div className="w-full md:w-[100%]  border-gray-300 px-3 mr-8 ">
            <AdminDetailsLoader />
          </div>
        ) : (
          <div className="w-full md:w-[100%]  border-gray-300 px-3 mr-8 ">
            <div className="border-b border-gray-300"></div>
            <div className="divide-y divide-gray-300">
              {/* Join Request ID */}
              {/* <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                <h2 className="text-sm font-medium text-gray-900">
                  Join Request ID
                </h2>
                <p className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                  {assessmentDetails?.join_vqa_request_id || "-"}
                </p>
              </div> */}
              {/* VQA ID */}
              {/* <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                <h2 className="text-sm font-medium text-gray-900">VQA ID</h2>
                <p className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                  {assessmentDetails?.vqa_id || "-"}
                </p>
              </div> */}
              {/* Request Status */}
              <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                <h2 className="text-sm font-medium text-gray-900">Scope</h2>
                <p className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                  <Capabilities
                    services={assessmentDetails?.capabilities}
                    icon={true}
                    showLimited={false}
                  />
                </p>
              </div>
              <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                <h2 className="text-sm font-medium text-gray-900">
                  Request Status
                </h2>
                <p className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                  <StatusBadges
                    statusName={assessmentDetails?.join_vqa_request_status}
                  />
                </p>
              </div>
              {/* Request Created On */}
              <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                <h2 className="text-sm font-medium text-gray-900">
                  Request Date
                </h2>
                <p className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                  {assessmentDetails?.join_vqa_request_created_on
                    ? dateFormat(assessmentDetails.join_vqa_request_created_on)
                    : "-"}
                </p>
              </div>
              {/* Sponsor Details */}
              <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                <h2 className="text-sm font-medium text-gray-900">
                  Sponsor Name
                </h2>
                <p className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                  {assessmentDetails?.sponsor?.sponsor_name || ""}
                </p>
              </div>
              <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                <h2 className="text-sm font-medium text-gray-900">
                  Contact Name
                </h2>
                <p className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                  {assessmentDetails?.sponsor?.sponsor_contact_name || "-"}
                </p>
              </div>
              <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                <h2 className="text-sm font-medium text-gray-900">Email</h2>
                <p className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                  {assessmentDetails?.sponsor?.sponsor_contact_email || "-"}
                </p>
              </div>{" "}
              <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4">
                <h2 className="text-sm font-medium text-gray-900">Phone</h2>
                <p className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0">
                  {assessmentDetails?.sponsor?.sponsor_contact_phone || "-"}
                </p>
              </div>
              {/* Additional Info (Read‑Only) */}
              <div className="px-0 py-4 sm:grid sm:grid-cols-3 sm:gap-4 ">
                <h2 className="text-sm font-medium text-gray-900">
                  Additional Info
                </h2>
                <textarea
                  className="mt-1 text-sm text-gray-700 sm:col-span-2 sm:mt-0 border border-gray-300 rounded-md p-2 w-full"
                  placeholder="Enter additional information here..."
                  value={additionalInfo}
                  readOnly
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* NotificationModal remains unchanged */}
      <NotificationModal
        open={openModal}
        setOpen={setOpenModal}
        title={modalTitle}
        desc={modalDescription}
        error={errorMessage}
      />
    </div>
  );
};
